import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import axios from "axios";

const API_URL = `${process.env.REACT_APP_API_URL}/services`;

export interface ApplicationService {
  id?: number;
  code?: string;
  name?: string;
  active?: boolean;
  applicationServiceParameter?: ApplicationServiceParameter[];
  parameters?: ApplicationServiceParameter[];
  createdAt?: string;
  updatedAt?: string;
}

export const INPUT_TYPES = ["TEXT", "PASSWORD", "NUMBER", "EMAIL", "URL"] as const;
export type InputType = typeof INPUT_TYPES[number];

export interface ApplicationServiceParameter {
  id?: number;
  parameterKey: string;
  label: string;
  inputType: InputType;
  required: boolean;
  placeholder: string;
}

export interface ApplicationServiceRequest {
  code: string;
  name: string;
  applicationServiceParameter?: ApplicationServiceParameter[];
  active?: boolean;
}

const normalizeService = (service: any): ApplicationService => ({
  ...service,
  applicationServiceParameter: service.parameters ?? service.applicationServiceParameter ?? [],
});

const getAllServices = async (): Promise<ApplicationService[]> => {
  const resp = await axios.get(API_URL);
  return resp.data.map(normalizeService);
};

const getServiceById = async (id: number): Promise<ApplicationService> => {
  const resp = await axios.get(`${API_URL}/${id}`);
  return normalizeService(resp.data);
};

const createService = async (data: ApplicationServiceRequest): Promise<ApplicationService> => {
  const resp = await axios.post(API_URL, data);
  return resp.data;
};

const toServiceRequestBody = (data: ApplicationServiceRequest): ApplicationServiceRequest => ({
  code: data.code,
  name: data.name,
  applicationServiceParameter: data.applicationServiceParameter ?? [],
  active: data.active,
});

const updateService = async (args: {
  id: number;
  data: ApplicationServiceRequest;
}): Promise<ApplicationService> => {
  const resp = await axios.put(`${API_URL}/${args.id}`, toServiceRequestBody(args.data));
  return resp.data;
};

const deleteService = async (id: number): Promise<void> => {
  await axios.delete(`${API_URL}/${id}`);
};

export function useApplicationServices() {
  const queryClient = useQueryClient();

  const servicesQuery = useQuery<ApplicationService[], Error>({
    queryKey: ["application-services"],
    queryFn: getAllServices,
  });

  const createServiceMutation = useMutation({
    mutationFn: createService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-services"] });
    },
  });

  const updateServiceMutation = useMutation({
    mutationFn: updateService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-services"] });
    },
  });

  const deleteServiceMutation = useMutation({
    mutationFn: deleteService,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["application-services"] });
    },
  });

  return {
    servicesQuery,
    getServiceById,
    createServiceMutation,
    updateServiceMutation,
    deleteServiceMutation,
  };
}
